import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Sidebar.css";

export interface RecentChat {
  id: string;
  name: string;
  timestamp?: string;
}

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  name?: string;
  recentChats?: RecentChat[];
  activeId?: string;
  onNewChat?: () => void;
  onLogout?: () => void;
  onDeleteChat?: (id: string) => void;
}

const formatRelativeTime = (timestamp?: string): string => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / (1000 * 60));
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) return "刚刚";
  if (diffMin < 60) return `${diffMin}分前`;
  if (diffHour < 24) return `${diffHour}时前`;
  if (diffDay === 1) return "昨天";
  if (diffDay < 30) return `${diffDay}天前`;
  return `${date.getMonth() + 1}月${date.getDate()}日`;
};

const SidebarToggleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="4" />
    <path d="M9 3v18" />
  </svg>
);

const EditPenIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

const MessageLineIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const LogOutLineIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggleCollapse,
  name,
  recentChats = [],
  activeId,
  onNewChat,
  onLogout,
  onDeleteChat,
}) => {
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const isNewChatActive = !recentChats.some((c) => c.id === activeId);

  const handleNewChatClick = () => {
    if (onNewChat) {
      onNewChat();
    } else {
      navigate("/");
    }
  };

  const handleLogoutClick = () => {
    if (onLogout) {
      onLogout();
    } else {
      navigate("/");
    }
  };

  const handleDeleteItem = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingId(id);
    setTimeout(() => {
      if (onDeleteChat) {
        onDeleteChat(id);
      }
      setDeletingId(null);
    }, 300);
  };

  return (
    <aside className={`sidebar ${collapsed ? "sidebar--collapsed" : ""}`}>
      <div className="sidebar__inner">
        {/* Header */}
        <div className="sidebar__header">
          <div className="sidebar__brand">
            <span className="sidebar__title">ChatBot</span>
          </div>
          <button
            type="button"
            className="sidebar__icon-btn"
            onClick={onToggleCollapse}
            title={collapsed ? "展开侧边栏" : "折叠侧边栏"}
          >
            <SidebarToggleIcon />
          </button>
        </div>

        {/* New Chat Button */}
        <button
          type="button"
          className={`sidebar__new-btn ${isNewChatActive ? "sidebar__new-btn--active" : ""}`}
          onClick={handleNewChatClick}
          title="发起新对话"
        >
          <EditPenIcon />
          <span className="sidebar__text-hideable">发起新对话</span>
        </button>

        {/* Recent Chats Section */}
        <div className="sidebar__section">
          <div className="sidebar__section-title sidebar__text-hideable">最近的对话</div>
          <div className="sidebar__history-list">
            {recentChats.length > 0 ? (
              recentChats.map((c) => (
                <div
                  key={c.id}
                  className={`sidebar__history-item ${
                    activeId === c.id ? "sidebar__history-item--active" : ""
                  } ${deletingId === c.id ? "sidebar__history-item--deleting" : ""}`}
                  onClick={() => navigate(`/chat/${c.id}`)}
                  title={c.name}
                >
                  <div className="sidebar__history-item-left">
                    <MessageLineIcon />
                    <span className="sidebar__history-name sidebar__text-hideable">
                      {c.name || "未命名对话"}
                    </span>
                  </div>

                  <div className="sidebar__history-item-right sidebar__text-hideable">
                    {c.timestamp && (
                      <span className="sidebar__history-time">
                        {formatRelativeTime(c.timestamp)}
                      </span>
                    )}
                    {onDeleteChat && (
                      <button
                        type="button"
                        className="sidebar__history-del"
                        onClick={(e) => handleDeleteItem(c.id, e)}
                        title="删除对话"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="sidebar__empty sidebar__text-hideable">
                暂无历史对话
              </div>
            )}
          </div>
        </div>

        {/* Footer User Info */}
        <div className="sidebar__footer">
          <div className="sidebar__footer-row">
            <div className="sidebar__user-avatar">{name ? name.slice(0, 1).toUpperCase() : "U"}</div>
            <div className="sidebar__user-info sidebar__text-hideable">
              <div className="sidebar__user-name">{name || "用户"}</div>
              <div className="sidebar__user-plan">Pro 会员</div>
            </div>
            <button
              type="button"
              className="sidebar__icon-btn sidebar__logout-btn sidebar__logout-btn--desktop"
              onClick={handleLogoutClick}
              title="退出登录"
            >
              <LogOutLineIcon />
            </button>
          </div>

          {/* 📍 折叠时平滑滑落至头像正下方的登出按钮 (头像 X 位置绝对不移动，垂直水平100%精准对齐) */}
          <button
            type="button"
            className="sidebar__icon-btn sidebar__logout-btn sidebar__logout-btn--collapsed"
            onClick={handleLogoutClick}
            title="退出登录"
          >
            <LogOutLineIcon />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
